import React, { useEffect, useMemo, useState } from "react";

/**
 * Confetti — tiny, dependency-free celebratory burst.
 *
 * Fires exactly once when `fire === true` becomes true. Renders ~24
 * absolutely-positioned squares that fall + drift + rotate via a
 * keyframe animation for ~1.6s and then unmount. Zero dependencies
 * (no canvas-confetti or third-party lib) — keeps the bundle small.
 *
 * Accessibility
 * -------------
 * • Honors `prefers-reduced-motion: reduce` — renders nothing in that
 *   case, matching the visual system-preference of the visitor.
 * • `aria-hidden="true"` on the wrapper; the surrounding success card
 *   still carries the semantic confirmation text.
 *
 * Placement
 * ---------
 * The component is `position: absolute` and expects to sit inside a
 * `position: relative` (or absolute/fixed) parent — e.g. the success
 * card. It draws pieces from the top-center of its parent outward.
 */
export default function Confetti({ fire }) {
  const [pieces, setPieces] = useState(null);
  const prefersReducedMotion = useMemo(() => {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!fire || prefersReducedMotion) {
      setPieces(null);
      return;
    }
    // Deterministic-ish spray: 24 pieces across a horizontal band with
    // varied colors, horizontal drift, rotation, and fall duration.
    const palette = ["#005CFF", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];
    const next = Array.from({ length: 24 }).map((_, i) => {
      const left = Math.random() * 100;               // 0-100%
      const drift = (Math.random() - 0.5) * 160;      // -80px to +80px horizontal drift
      const rotate = (Math.random() - 0.5) * 720;     // up to ±360deg spin
      const duration = 1.2 + Math.random() * 0.9;     // 1.2-2.1s
      const delay = Math.random() * 0.25;             // slight stagger
      const size = 6 + Math.random() * 6;             // 6-12px
      const color = palette[i % palette.length];
      return { id: i, left, drift, rotate, duration, delay, size, color };
    });
    setPieces(next);
    // Remove the pieces from the DOM after the longest possible animation
    // finishes (2.1s + 0.25s stagger + a bit of buffer). Prevents lingering
    // absolutely-positioned nodes that would intercept clicks or fights
    // with focus rings.
    const t = setTimeout(() => setPieces(null), 2500);
    return () => clearTimeout(t);
  }, [fire, prefersReducedMotion]);

  if (!pieces) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="verify-confetti"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: "inherit",
      }}
    >
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: 2,
            transform: "translate(-50%, -20px)",
            opacity: 0,
            animation: `sf-confetti-fall ${p.duration}s ease-out ${p.delay}s forwards`,
            // CSS var lets the keyframe use per-piece drift + rotation:
            "--sf-drift": `${p.drift}px`,
            "--sf-rotate": `${p.rotate}deg`,
          }}
        />
      ))}
      <style>{`
        @keyframes sf-confetti-fall {
          0%   { transform: translate(-50%, -20px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% {
            transform: translate(calc(-50% + var(--sf-drift)), 220px) rotate(var(--sf-rotate));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
